import { AlertTriangle, Volume2, VolumeX, Wifi, WifiOff, PanelLeft, PanelRight } from "lucide-react";
import LogoGalarya from "@/components/LogoGalarya";

// Contador legible: punto de color (o ícono) + número + etiqueta siempre visible en desktop.
function Counter({ dot, icon: Icon, n, label, tone, testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {Icon ? (
        <Icon strokeWidth={2} className={`h-4 w-4 ${tone}`} />
      ) : (
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      )}
      <span className={`text-base font-bold tabular-nums ${tone || "text-slate-700 dark:text-slate-100"}`}>{n}</span>
      <span className="hidden sm:inline text-sm text-slate-500 dark:text-slate-400">{label}</span>
    </span>
  );
}

export default function TopBar({
  counts, live, fechaHora, audioOn, onToggleAudio, onTestSound,
  coberturaCritica, onVerCobertura, onToggleCola, onToggleContexto, ctxAbierto, noche, onToggleNoche,
}) {
  return (
    <header className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <div className="flex items-center justify-between gap-4 px-4 py-2.5">
        {/* Identidad + contadores */}
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <button data-testid="btn-toggle-cola" onClick={onToggleCola} className="lg:hidden text-slate-500 dark:text-slate-300 shrink-0">
            <PanelLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 shrink-0">
            <LogoGalarya className="h-8 w-auto max-w-[10.5rem]" />
            <div className="hidden md:flex flex-col leading-tight">
              <span className="text-base font-bold text-[#012472] dark:text-slate-100">Centro de Monitoreo</span>
              <span data-testid="topbar-fecha-hora" className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{fechaHora}</span>
            </div>
          </div>
          <div className="hidden lg:block h-9 w-px bg-slate-200 dark:bg-slate-700 shrink-0" />
          <div className="flex items-center gap-3 md:gap-4 flex-wrap">
            <Counter dot="bg-red-500" n={counts.altaSinTomar} label="Alta" tone="text-red-600 dark:text-red-400" testId="c-alta" />
            <span className="hidden md:inline text-slate-300 dark:text-slate-600">·</span>
            <Counter dot="bg-amber-500" n={counts.mediaSinTomar} label="Media" tone="text-amber-600 dark:text-amber-400" testId="c-media" />
            <span className="hidden md:inline text-slate-300 dark:text-slate-600">·</span>
            <Counter icon={AlertTriangle} n={counts.coberturaCritica} label="Cobertura crítica" tone="text-[#9B2C2C] dark:text-[#F2B8B8]" testId="c-cobertura" />
            <span className="hidden md:inline text-slate-300 dark:text-slate-600">·</span>
            <Counter dot="bg-slate-400" n={counts.enAtencion} label="En atención" tone="text-slate-600 dark:text-slate-300" testId="c-atencion" />
          </div>
        </div>
        {/* Controles siempre visibles */}
        <div className="flex items-center gap-2 md:gap-2.5 shrink-0">
          <span data-testid="live-indicator" title={live ? "Canal en vivo (sondeo activo)" : "SIN señal en vivo"}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              live ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                   : "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 animate-pulse"}`}>
            {live ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {live ? "Live" : "SIN SEÑAL"}
          </span>
          <button data-testid="toggle-audio" onClick={onToggleAudio}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              audioOn ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"}`}>
            {audioOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            <span className="hidden sm:inline">{audioOn ? "Sonido de alertas ON" : "Sonido de alertas"}</span>
          </button>
          {audioOn && <button data-testid="btn-probar-sonido" onClick={onTestSound} className="hidden md:inline text-xs text-slate-400 hover:text-slate-600">Probar</button>}
          <button data-testid="toggle-noche" onClick={onToggleNoche} className="text-slate-500 dark:text-slate-300 text-sm">{noche ? "☀" : "🌙"}</button>
          <button data-testid="btn-toggle-contexto" onClick={onToggleContexto} title={ctxAbierto ? "Ocultar panel de contexto" : "Mostrar panel de contexto"}
            className={`inline-flex items-center justify-center rounded-lg p-1 transition-colors ${
              ctxAbierto ? "text-[#012472] dark:text-[#80B3FF] bg-[#01CBE3]/10" : "text-slate-400 dark:text-slate-500 hover:text-slate-600"}`}>
            <PanelRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      {coberturaCritica > 0 && (
        <div data-testid="banda-cobertura" className="flex items-center justify-between gap-3 bg-[#9B2C2C]/10 dark:bg-[#9B2C2C]/25 border-t border-[#9B2C2C]/30 px-4 py-1.5">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#9B2C2C] dark:text-[#F2B8B8]">
            <AlertTriangle className="h-4 w-4" /> {coberturaCritica} cobertura(s) crítica(s) sin atender
          </span>
          <button data-testid="btn-ver-cobertura" onClick={onVerCobertura} className="text-xs font-semibold underline text-[#9B2C2C] dark:text-[#F2B8B8]">Ver</button>
        </div>
      )}
    </header>
  );
}
